// =====================================================================
// treatmentPlanPdf — declarative React-PDF generator.
// =====================================================================
//
// Architecture notes (read this before editing):
//
//   1. Layout is DECLARATIVE. Components compose with flex; the engine
//      handles positioning, page breaks, and page numbering. DO NOT
//      introduce y-cursor math here.
//
//   2. Tokens live in TOK below — single source of truth for spacing,
//      colors, type sizes. Magic numbers in styles are a code smell.
//
//   3. Section order is JSX order in <TxPlanDocument>. To reorder
//      sections, move the JSX line.
//
//   4. Page-break behavior is controlled by `wrap={false}` on Views
//      that should stay together (summary, payment rows, visit blocks,
//      acknowledgment + signature unit). The signature block is pinned
//      to the bottom of its page via a flex-grow spacer just above it.
//
//   5. NO bold display weights. Heaviest weight is "medium" (Inter 500).
//      Hierarchy comes from size + color + position, not visual weight.
//
//   6. NO tinted backgrounds, NO card borders, NO hero blocks. Hairlines
//      and whitespace do all structural separation.
//
//   7. Inputs validated at the boundary in `buildTreatmentPlanPdf`.
//      Missing doctor name throws verbatim spec copy.
// =====================================================================

import React from "react";
import {
  Document,
  Page,
  View,
  Text,
  Image,
  StyleSheet,
  Font,
  pdf,
} from "@react-pdf/renderer";
import { expandPlanToVisits } from "@/lib/planVisits";
import { formatFlexibleSummary } from "@/lib/paymentSchedule";
import { orthoSystemClauses } from "@/lib/orthoContractClauses";

// ─── Font registration ───────────────────────────────────────────────
// Inter ships only weights/styles we use (regular 400, italic 400,
// medium 500). Loaded from the @fontsource/inter package so the font is bundled — no
// runtime CDN dependency. WOFF format is supported by @react-pdf/
// renderer 3.x.
import InterRegular from "@fontsource/inter/files/inter-latin-400-normal.woff";
import InterRegularItalic from "@fontsource/inter/files/inter-latin-400-italic.woff";
import InterMedium  from "@fontsource/inter/files/inter-latin-500-normal.woff";

Font.register({
  family: "Inter",
  fonts: [
    { src: InterRegular, fontWeight: 400 },
    { src: InterRegularItalic, fontWeight: 400, fontStyle: "italic" },
    { src: InterMedium,  fontWeight: 500 },
  ],
});

// ─── Design tokens ───────────────────────────────────────────────────
// Institutional clinical look: navy accent for filled table headers and
// section-label trailing rules, light tinted row for GRAND TOTAL, no
// other decorative color. Spacing is denser than spa-clean to match
// "real practice document" feel.
const TOK = {
  color: {
    primary:   "#1a2332",
    secondary: "#6b7280",
    hairline:  "#d1d5db",
    accent:    "#1e3a5f",   // navy — filled headers + section-label rules
    tintedRow: "#f3f4f6",   // GRAND TOTAL row
    white:     "#ffffff",   // text on filled accent rows
    logoTint:  "#a8e3df",   // fallback logo box only
  },
  s: { 4: 4, 6: 6, 8: 8, 10: 10, 12: 12, 14: 14, 16: 16, 20: 20, 24: 24, 48: 48 },
  font: {
    title:         22,
    clinicName:    14,
    sectionLabel:  11,
    metaLabel:     10,
    metaValue:     10,
    tableHead:      9.5,
    tableBody:     10,
    grandTotal:    11,
    visitNum:      11,
    body:          10,
    footnote:       9,
    confLabel:     10,
    sigName:       10,
    sigRole:        9,
    fineprint:      8,
    contactLine:    9,
  },
  weight: {
    regular: 400,
    medium:  500,
  },
};

const DEFAULT_QUOTE_VALIDITY_DAYS = 30;

// ─── StyleSheet ──────────────────────────────────────────────────────
const styles = StyleSheet.create({
  page: {
    fontFamily: "Inter",
    fontSize: TOK.font.body,
    color: TOK.color.primary,
    padding: TOK.s[48],
    flexDirection: "column",
  },

  // Letterhead band — centered content. Logo, then centered clinic name
  // and contact lines.
  letterhead: {
    minHeight: 80,
    paddingBottom: TOK.s[16],
    borderBottomWidth: 1,
    borderBottomColor: TOK.color.hairline,
    alignItems: "center",
  },
  letterheadLogo: { width: 56, height: 56, marginBottom: TOK.s[8] },
  letterheadLogoFallback: {
    width: 56, height: 56, marginBottom: TOK.s[8],
    backgroundColor: TOK.color.logoTint,
    alignItems: "center", justifyContent: "center",
  },
  letterheadLogoFallbackText: {
    fontFamily: "Inter", fontWeight: TOK.weight.medium,
    fontSize: 8, color: TOK.color.primary,
  },
  letterheadClinic: {
    fontFamily: "Inter",
    fontWeight: TOK.weight.medium,
    fontSize: TOK.font.clinicName,
    color: TOK.color.primary,
    textAlign: "center",
    marginBottom: 4,
  },
  letterheadContact: {
    fontSize: TOK.font.contactLine,
    color: TOK.color.secondary,
    textAlign: "center",
    marginBottom: 2,
  },

  // Document title + metadata info table
  title: {
    fontSize: TOK.font.title,
    color: TOK.color.primary,
    textAlign: "center",
    letterSpacing: 1.3,           // ~0.06em at 22pt
    marginTop: TOK.s[24],
    marginBottom: TOK.s[20],
  },
  infoTable: {
    borderWidth: 1,
    borderColor: TOK.color.hairline,
  },
  infoRow: { flexDirection: "row" },
  infoRowDivider: { borderTopWidth: 1, borderTopColor: TOK.color.hairline },
  infoLabelCell: {
    width: 160,
    paddingVertical: TOK.s[6],
    paddingHorizontal: TOK.s[10],
    borderRightWidth: 1,
    borderRightColor: TOK.color.hairline,
    fontFamily: "Inter",
    fontWeight: TOK.weight.medium,
    fontSize: TOK.font.metaLabel,
    color: TOK.color.primary,
  },
  infoValueCell: {
    flex: 1,
    paddingVertical: TOK.s[6],
    paddingHorizontal: TOK.s[10],
    fontSize: TOK.font.metaValue,
    color: TOK.color.primary,
  },
  infoSubValue: {
    fontSize: TOK.font.contactLine,
    color: TOK.color.secondary,
    marginTop: 2,
  },

  // Section label with accent trailing rule
  section: { marginTop: TOK.s[24] },
  sectionLabelRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: TOK.s[12],
  },
  sectionLabelText: {
    fontFamily: "Inter",
    fontWeight: TOK.weight.medium,
    fontSize: TOK.font.sectionLabel,
    color: TOK.color.primary,
    letterSpacing: 0.9,          // ~0.08em at 11pt
    marginRight: TOK.s[12],
  },
  sectionRule: { flex: 1, height: 1, backgroundColor: TOK.color.accent },

  // Generic table chrome shared by procedure / payment / visit tables
  table: {
    borderWidth: 1,
    borderColor: TOK.color.hairline,
  },
  tableHeadFilled: {
    flexDirection: "row",
    backgroundColor: TOK.color.accent,
  },
  tableHeadFilledCell: {
    fontFamily: "Inter",
    fontWeight: TOK.weight.medium,
    fontSize: TOK.font.tableHead,
    color: TOK.color.white,
    letterSpacing: 0.5,          // ~0.05em at 9.5pt
    paddingVertical: TOK.s[8],
    paddingHorizontal: TOK.s[10],
  },
  tableBodyRow: {
    flexDirection: "row",
    borderTopWidth: 1,
    borderTopColor: TOK.color.hairline,
  },
  tableBodyRowFirst: {
    flexDirection: "row",
    // First body row sits flush against the filled header — no top
    // hairline since the header has its own bottom edge.
  },
  tableBodyCell: {
    paddingVertical: TOK.s[6],
    paddingHorizontal: TOK.s[10],
    fontSize: TOK.font.tableBody,
    color: TOK.color.primary,
  },
  // GRAND TOTAL row in the procedure table
  grandTotalRow: {
    flexDirection: "row",
    backgroundColor: TOK.color.tintedRow,
    borderTopWidth: 1,
    borderTopColor: TOK.color.hairline,
  },
  grandTotalLabelCell: {
    paddingVertical: TOK.s[8],
    paddingHorizontal: TOK.s[10],
    textAlign: "right",
    fontFamily: "Inter",
    fontWeight: TOK.weight.medium,
    fontSize: TOK.font.grandTotal,
    color: TOK.color.primary,
  },
  grandTotalValueCell: {
    paddingVertical: TOK.s[8],
    paddingHorizontal: TOK.s[10],
    textAlign: "right",
    fontFamily: "Inter",
    fontWeight: TOK.weight.medium,
    fontSize: TOK.font.grandTotal,
    color: TOK.color.primary,
  },

  // Acknowledgment + footnotes
  ackText: {
    fontSize: TOK.font.body,
    color: TOK.color.primary,
    lineHeight: 1.4,
  },
  footnotesWrap: { marginTop: TOK.s[12] },
  footnoteLine: {
    fontSize: TOK.font.footnote,
    color: TOK.color.secondary,
    lineHeight: 1.45,
    marginBottom: 2,
  },

  // Conforme block (page-bottom unit)
  confLabel: {
    fontFamily: "Inter",
    fontWeight: TOK.weight.medium,
    fontSize: TOK.font.confLabel,
    color: TOK.color.primary,
    marginBottom: TOK.s[8],
  },
  signatureRow: { flexDirection: "row", gap: 32 },
  signatureCol: { flex: 1 },
  signatureSpacer: { height: 28, justifyContent: "flex-end" },
  signatureImage: { width: 140, height: 28, objectFit: "contain", marginBottom: 2 },
  signatureLine: { height: 1, backgroundColor: TOK.color.primary, marginBottom: TOK.s[8] },
  signatureName: {
    fontFamily: "Inter",
    fontWeight: TOK.weight.medium,
    fontSize: TOK.font.sigName,
    color: TOK.color.primary,
    marginBottom: 4,
  },
  signaturePrc: {
    fontSize: TOK.font.sigRole,
    color: TOK.color.secondary,
    marginBottom: 4,
  },
  signatureRole: {
    fontFamily: "Inter",
    fontWeight: TOK.weight.medium,
    fontSize: TOK.font.sigRole,
    color: TOK.color.secondary,
    letterSpacing: 0.5,
  },
  signedMeta: {
    fontSize: TOK.font.fineprint,
    color: TOK.color.secondary,
    marginTop: 3,
  },

  // Footer fine print
  footerRule:  { height: 1, backgroundColor: TOK.color.hairline, marginTop: TOK.s[16] },
  footerInner: { paddingTop: TOK.s[12] },
  footerLine:  { fontSize: TOK.font.fineprint, color: TOK.color.secondary, marginBottom: 2, lineHeight: 1.5 },

  // Flex-grow spacer pinning the Conforme block to the page bottom.
  spacer: { flexGrow: 1 },
});

// ─── Pure helpers ────────────────────────────────────────────────────
function formatPHP(centavos) {
  return "Php " + Math.round((centavos ?? 0) / 100).toLocaleString("en-PH");
}

function fullName(p) {
  if (!p) return "—";
  if (p.full_name) return p.full_name;
  return [p.first_name, p.last_name].filter(Boolean).join(" ").trim() || "—";
}

function formatLongDate(dateLike) {
  if (!dateLike) return "";
  const d = dateLike instanceof Date ? dateLike : new Date(dateLike);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-PH", { year: "numeric", month: "long", day: "numeric" });
}

function formatContractDate(dateLike) {
  if (!dateLike) return "—";
  const d = dateLike instanceof Date ? dateLike : new Date(dateLike);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

function formatContractPHP(centavos) {
  const n = Number(centavos || 0) / 100;
  return `Php ${n.toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function compact(value, fallback = "—") {
  const text = value == null ? "" : String(value).trim();
  return text || fallback;
}

function patientAgeAt(patient, asOf) {
  if (!patient?.date_of_birth) return null;
  const birth = new Date(patient.date_of_birth);
  const today = asOf instanceof Date ? asOf : new Date(asOf || Date.now());
  if (Number.isNaN(birth.getTime()) || Number.isNaN(today.getTime())) return null;
  let age = today.getFullYear() - birth.getFullYear();
  const monthDelta = today.getMonth() - birth.getMonth();
  if (monthDelta < 0 || (monthDelta === 0 && today.getDate() < birth.getDate())) age -= 1;
  return age;
}

function guardianInfo(patient) {
  return {
    name: compact(patient?.guardian_name || patient?.guardian_full_name || patient?.emergency_contact_name, ""),
    relationship: compact(patient?.guardian_relationship || patient?.relationship_to_guardian || patient?.emergency_contact_relationship || patient?.relationship_to_household, ""),
    contact: compact(patient?.guardian_contact_number || patient?.guardian_phone || patient?.guardian_contact || patient?.emergency_contact_phone, ""),
  };
}

function contractRefForCase(orthoCase) {
  const explicit = orthoCase?.case_code || orthoCase?.contract_ref || orthoCase?.case_ref || orthoCase?.code;
  if (explicit) return String(explicit);
  const id = String(orthoCase?.id || "").replace(/-/g, "").slice(0, 6).toUpperCase();
  return id ? `PT-${id}` : "PT-______";
}

function statusLabel(value) {
  return labelize(value || "active");
}

function systemDisplayName(orthoCase, contract) {
  return compact(
    orthoCase?.system_name
      || orthoCase?.appliance_system
      || orthoCase?.system
      || contract?.system,
    "Metal Conventional",
  );
}

function estimatedDurationLabel(orthoCase) {
  const months = Number(
    orthoCase?.estimated_treatment_months
      || orthoCase?.estimated_treatment_duration_months
      || orthoCase?.estimated_duration_months
      || 0,
  );
  return months > 0 ? `Approx. ${months} months` : "Approx. — months";
}

export function buildOrthoContractViewModel({
  contract,
  patient,
  clinic,
  doctorName,
  doctorPrcLicense,
  issuedAt = new Date(),
}) {
  if (!contract) {
    throw new Error("Ortho contract data is required to generate a PDF.");
  }
  const orthoCase = contract?.case || contract || {};
  if (String(orthoCase.status || "").toLowerCase() === "draft") {
    throw new Error("Ortho contract cannot be generated while the case is still in draft.");
  }

  const billing = contract?.billing || {};
  const contractIssuedAt = billing.contract_generated_at || issuedAt;
  const age = Number.isFinite(Number(billing.patient_age_at_contract))
    ? Number(billing.patient_age_at_contract)
    : patientAgeAt(patient, contractIssuedAt);
  const minor = typeof billing.minor_at_contract === "boolean"
    ? billing.minor_at_contract
    : age != null && age < 18;
  const guardian = {
    ...guardianInfo(patient),
    name: compact(billing.guardian_full_name || patient?.guardian_name || patient?.guardian_full_name || patient?.emergency_contact_name, ""),
    relationship: compact(billing.guardian_relationship || patient?.guardian_relationship || patient?.relationship_to_guardian || patient?.emergency_contact_relationship || patient?.relationship_to_household, ""),
  };
  if (minor && (!guardian.name || !guardian.relationship)) {
    throw new Error("Guardian full name and relationship to patient are required for minor orthodontic contracts.");
  }

  const packagePrice = Number(billing.package_price_centavos ?? orthoCase.package_price_centavos ?? orthoCase.total_fee_centavos ?? 0);
  const downPayment = Number(billing.down_payment_centavos ?? orthoCase.down_payment_centavos ?? 0);
  const remaining = Math.max(packagePrice - downPayment, 0);
  const visits = Number(orthoCase.estimated_total_visits || billing.estimated_total_visits || 0);
  const installments = Number(billing.number_of_payments || billing.number_of_installments || billing.estimated_total_visits || visits || 0);
  const perVisit = Number(billing.per_visit_charge_centavos || billing.per_payment_charge_centavos || 0);
  const preferredPayment = Number(billing.preferred_payment_centavos || 0);
  const finalPayment = Number(billing.final_payment_centavos || perVisit || 0);
  const systemName = systemDisplayName(orthoCase, contract);
  const contractRef = contractRefForCase(orthoCase);
  const clinicName = compact(clinic?.clinic_name || clinic?.name || clinic?.slug, "Dental OS");
  const resolvedDoctorName = compact(doctorName || orthoCase.doctor_name || orthoCase.attending_dentist || orthoCase.primary_doctor_name, "");
  const resolvedDoctorPrc = compact(doctorPrcLicense || orthoCase.doctor_prc_license || orthoCase.prc_license_number || orthoCase.prc_license, "");
  if (!resolvedDoctorName) {
    throw new Error("Attending dentist full name is missing. Clinic admin must complete the dentist profile before the Ortho contract can be generated.");
  }
  if (!resolvedDoctorPrc) {
    throw new Error("Attending dentist PRC License Number is missing. Clinic admin must complete the dentist profile before the Ortho contract can be generated.");
  }
  const patientRows = [
    [
      { label: "Patient Name", value: fullName(patient) },
      { label: "Date of Birth", value: formatContractDate(patient?.date_of_birth) },
      { label: "Date Issued", value: formatContractDate(contractIssuedAt) },
    ],
  ];

  if (minor) {
    patientRows.push([
      { label: "Guardian Name", value: guardian.name },
      { label: "Relationship", value: guardian.relationship },
      { label: "Patient Age", value: age != null ? String(age) : "—" },
    ]);
  }

  patientRows.push(
    [
      { label: "Attending Dentist", value: resolvedDoctorName },
      { label: "PRC License Number", value: resolvedDoctorPrc },
      { label: "Chief Complaint", value: compact(orthoCase.chief_complaint || contract?.chief_complaint) },
    ],
    [
      { label: "Contract Ref", value: contractRef },
      { label: "Case Type", value: labelize(orthoCase.case_type || contract?.case_type || "fixed") },
      { label: "Status", value: statusLabel(orthoCase.status || orthoCase.clinical_status) },
    ],
    [
      { label: "Estimated Treatment Duration", value: estimatedDurationLabel(orthoCase) },
      { label: "System", value: systemName },
      { label: "Contact Number", value: compact(patient?.phone || patient?.contact_number || patient?.mobile_number) },
    ],
  );

  return {
    header: {
      clinicName,
      logoDataUrl: clinic?.logo_data_url || null,
    },
    systemName,
    systemClauses: orthoSystemClauses(systemName),
    clinicNotes: compact(orthoCase.notes || orthoCase.clinic_notes, "No additional notes recorded."),
    acknowledgement: "I acknowledge that the treatment terms, payment terms, visit obligations, appliance care requirements, and receipt policy were explained to me. I understand that orthodontic treatment requires cooperation, timely attendance, good oral hygiene, and compliance with clinic instructions.",
    patientRows,
    billingRows: [
      { label: "Package Price", value: formatContractPHP(packagePrice), inverted: false },
      { label: "Down Payment", value: formatContractPHP(downPayment), inverted: false },
      { label: "Remaining Balance", value: formatContractPHP(remaining), inverted: false },
      { label: "Estimated Total Visits", value: visits ? `${visits}` : "—", inverted: false },
      { label: "Number of Installments", value: installments ? `${installments}` : "—", inverted: false },
      {
        label: preferredPayment > 0 ? "Preferred Regular Payment" : "Per-Visit Payment Charge",
        value: formatContractPHP(perVisit),
        inverted: true,
      },
      ...(finalPayment > 0 && finalPayment !== perVisit
        ? [{ label: "Final Regular Payment", value: formatContractPHP(finalPayment), inverted: false }]
        : []),
    ],
    signatures: {
      patientRole: minor ? "Patient / Guardian Signature" : "Patient Signature",
      patientName: minor ? guardian.name : fullName(patient),
      doctorName: resolvedDoctorName,
    },
    footer: { contractRef },
  };
}

// Strip a leading "PRC-" or "PRC " prefix from a stored license string,
// then trim. Prevents "PRC PRC-1234567" duplication when the data layer
// already has the prefix baked in.
function normalizePrc(raw) {
  if (raw == null) return null;
  const s = String(raw).trim().replace(/^PRC[-\s]+/i, "").trim();
  return s || null;
}

const CATEGORY_CODE_MAP = {
  Prosthodontic:  "PROSTHO",
  Restorative:    "RESTOR",
  Endodontic:     "ENDO",
  Periodontal:    "PERIO",
  Periodontic:    "PERIO",
  "Oral Surgery": "SURGERY",
  Cosmetic:       "COSM",
  Implant:        "IMPLANT",
  Diagnostic:     "DIAG",
  Preventive:     "PREV",
  Emergency:      "EMR",
  Pediatric:      "PEDO",
  Orthodontic:    "ORTHO",
};
function categoryCode(category) {
  if (!category) return "—";
  return CATEGORY_CODE_MAP[category] || category.toUpperCase().slice(0, 8);
}

function teethListString(item) {
  if (Array.isArray(item.teeth) && item.teeth.length >= 2) {
    return item.teeth
      .map((t) => (typeof t === "object" ? t?.tooth_number : t))
      .filter(Boolean)
      .join(", ");
  }
  return item.tooth_number ? String(item.tooth_number) : "—";
}

function buildSinglePlanRows(items) {
  return items.map((it) => {
    const teethArr = Array.isArray(it.teeth) ? it.teeth : [];
    const isMulti  = teethArr.length >= 2;
    const units    = isMulti ? teethArr.length : 1;
    const total    = it.fee_centavos ?? 0;
    const unitFee  = isMulti
      ? (it.material?.unit_fee_centavos ?? Math.round(total / Math.max(1, units)))
      : total;
    return {
      tooth: teethListString(it),
      procedure: it.display_label || it.procedure_name || "Procedure",
      code: categoryCode(it.procedure_category),
      units, unitFee, total,
      discountAmount: Number(it.discount_amount_centavos || 0),
      discountPct:    it.discount_pct == null ? null : Number(it.discount_pct),
    };
  });
}

function rowNetCentavos(row) {
  const gross = Number(row.total || 0);
  if (row.discountAmount > 0) return Math.max(0, gross - row.discountAmount);
  if (row.discountPct && row.discountPct > 0) {
    return Math.max(0, Math.round(gross * (1 - row.discountPct / 100)));
  }
  return gross;
}

function sortRowsByTooth(rows) {
  return [...rows].sort((a, b) => {
    const ta = a.tooth.split(",")[0].trim();
    const tb = b.tooth.split(",")[0].trim();
    const na = ta === "—" ? Infinity : Number(ta);
    const nb = tb === "—" ? Infinity : Number(tb);
    if (na !== nb) return na - nb;
    return a.procedure.localeCompare(b.procedure);
  });
}

function openItems(plan) {
  return (plan?.items ?? []).filter((i) => i.status !== "Declined");
}

function formatOffset(days) {
  const n = Math.max(0, Math.floor(days || 0));
  if (n === 0) return "0 days";
  if (n % 7 === 0) {
    const w = n / 7;
    return `${w} ${w === 1 ? "week" : "weeks"}`;
  }
  return `${n} ${n === 1 ? "day" : "days"}`;
}

function phaseTimingNote(plan, n) {
  if (n === 1) return "begins immediately";
  const meta = plan?.phase_meta?.[String(n)];
  if (!meta || !meta.offset_days) return null;
  const refLabel = plan?.phase_names?.[String(meta.after_phase)] || `Visit ${meta.after_phase}`;
  return `${formatOffset(meta.offset_days)} after ${refLabel}`;
}

function visitSections(plan, items) {
  const used = new Set();
  const visits = expandPlanToVisits({ items });
  for (const v of visits) {
    if (v.phase_number != null && Number(v.phase_number) > 0) used.add(Number(v.phase_number));
  }
  for (const i of items) {
    if (i.phase_number != null && Number(i.phase_number) > 0) used.add(Number(i.phase_number));
  }
  for (const k of Object.keys(plan?.phase_names || {})) {
    const n = Number(k);
    if (Number.isFinite(n) && n > 0) used.add(n);
  }
  const sorted = [...used].sort((a, b) => a - b);
  return sorted.map((n) => {
    const visitsHere = visits.filter((v) => Number(v.phase_number) === n);
    const chairMin = visitsHere.reduce((s, v) => s + (Number(v.chair_min) || 0), 0);
    // Prefer the appointment duration the board shows (booked → typed →
    // chair + setup), passed in as plan.phase_durations. Falls back to the
    // raw chair sum for older callers that don't supply it.
    const planned = Number(plan?.phase_durations?.[String(n)]);
    return {
      timing:   phaseTimingNote(plan, n),
      chairMin,
      durationMin: Number.isFinite(planned) && planned > 0 ? planned : chairMin,
      visits:   visitsHere,
    };
  });
}

const PLAN_ORDER = ["per_phase", "50_50", "full", "custom"];
const PLAN_TITLE = {
  per_phase: "Even split",
  "50_50":   "Deposit + Balance",
  full:      "Pay in Full",
  custom:    "Flexible",
};

function planBodyLine(plan, { netTotal, phaseCount, flexMinDownPct }) {
  if (plan === "per_phase") {
    const per = Math.round(netTotal / Math.max(1, phaseCount));
    return phaseCount > 1
      ? `${formatPHP(per)} × ${phaseCount} visits`
      : `${formatPHP(netTotal)} at the only visit`;
  }
  if (plan === "50_50") {
    return phaseCount > 1
      ? "50/50 across visits"
      : `${formatPHP(netTotal)} at the only visit`;
  }
  if (plan === "full") {
    // Pay in Full — entire total collected upfront on the first visit.
    return `${formatPHP(netTotal)} at the first visit`;
  }
  if (plan === "custom") {
    return formatFlexibleSummary(flexMinDownPct, netTotal);
  }
  return formatPHP(netTotal);
}

// ─── Components ──────────────────────────────────────────────────────
function Letterhead({ clinic }) {
  const clinicName =
       clinic?.clinic_name
    || clinic?.name
    || (clinic?.slug ? clinic.slug.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : null)
    || "Dental Clinic";
  const contact = [clinic?.address, clinic?.phone].filter(Boolean).join("  ·  ");

  return (
    <View style={styles.letterhead} wrap={false}>
      {clinic?.logo_data_url ? (
        <Image src={clinic.logo_data_url} style={styles.letterheadLogo} />
      ) : (
        <View style={styles.letterheadLogoFallback}>
          <Text style={styles.letterheadLogoFallbackText}>DENTAL OS</Text>
        </View>
      )}
      <Text style={styles.letterheadClinic}>{clinicName}</Text>
      {contact ? <Text style={styles.letterheadContact}>{contact}</Text> : null}
    </View>
  );
}

function DoctorIdentityValue({ doctorName, doctorPrcLicense }) {
  const prc = normalizePrc(doctorPrcLicense);
  return (
    <Text style={styles.infoValueCell}>
      {doctorName}
      {prc ? <Text style={styles.infoSubValue}>{`\nPRC ${prc}`}</Text> : null}
    </Text>
  );
}

function TitleBlock({ patient, doctorName, doctorPrcLicense, dateStr, mode, accepted = false }) {
  // Bordered 2-column info table — labels in the left column, values
  // in the right. Atomic with the title via wrap={false} on the outer
  // View so the title never orphans on a page break.
  const rows = [
    ["Patient", fullName(patient)],
    ["Date", dateStr],
  ];
  if (doctorName) {
    rows.push([
      "Attending Dentist",
      <DoctorIdentityValue
        doctorName={doctorName}
        doctorPrcLicense={doctorPrcLicense}
      />,
    ]);
  }

  // A plan isn't a "Treatment Plan" on the patient's document until they
  // accept it — before that it's a proposal. Compare mode presents
  // options to choose from, which are proposals by definition.
  const noun = accepted ? "TREATMENT PLAN" : "TREATMENT PROPOSAL";
  const title = mode === "compare" ? `${noun} OPTIONS` : noun;

  return (
    <View wrap={false}>
      <Text style={styles.title}>
        {title}
      </Text>
      <View style={styles.infoTable}>
        {rows.map(([label, value], i) => (
          <View
            key={label}
            style={[styles.infoRow, i > 0 ? styles.infoRowDivider : null]}
          >
            <Text style={styles.infoLabelCell}>{label}</Text>
            {React.isValidElement(value) ? value : (
              <Text style={styles.infoValueCell}>{value}</Text>
            )}
          </View>
        ))}
      </View>
    </View>
  );
}

function OrthoContractDocument({ contract, patient, clinic, doctorName, doctorPrcLicense }) {
  const model = buildOrthoContractViewModel({
    contract,
    patient,
    clinic,
    doctorName,
    doctorPrcLicense,
  });

  return (
    <Document title="Ortho Contract">
      <Page size="A4" style={contractStyles.page}>
        <ContractHeader model={model} />

        <ContractSection number="1" title="PATIENT AND CASE INFORMATION">
          <View style={contractStyles.fieldTable}>
            {model.patientRows.map((row, rowIndex) => (
              <View key={`patient-row-${rowIndex}`} style={contractStyles.fieldRow}>
                {row.map((field) => <ContractField key={field.label} field={field} />)}
              </View>
            ))}
          </View>
        </ContractSection>

        <ContractSection number="2" title="BILLING TERMS">
          <View style={contractStyles.billingTable}>
            {model.billingRows.map((row, index) => <ContractBillingRow key={row.label} row={row} first={index === 0} />)}
          </View>
        </ContractSection>

        <ContractSection number="3" title="GENERAL TERMS AND PATIENT OBLIGATIONS">
          <NumberedClauses clauses={GENERAL_ORTHO_CONTRACT_CLAUSES} />
        </ContractSection>

        <ContractSection number="4" title={`SYSTEM-SPECIFIC COMPLIANCE OBLIGATIONS  (${model.systemName})`}>
          <NumberedClauses clauses={model.systemClauses} />
        </ContractSection>

        <ContractSection number="5" title="TREATMENT DURATION DISCLAIMER">
          <NumberedClauses clauses={TREATMENT_DURATION_CLAUSES} />
        </ContractSection>

        <ContractSection number="6" title="PAYMENT AND RECEIPT POLICY">
          <NumberedClauses clauses={PAYMENT_RECEIPT_CLAUSES} />
        </ContractSection>

        <ContractSection number="7" title="CLINIC NOTES">
          <Text style={contractStyles.grayBox}>{model.clinicNotes}</Text>
        </ContractSection>

        <ContractSection number="8" title="ACKNOWLEDGEMENT">
          <Text style={contractStyles.grayBox}>{model.acknowledgement}</Text>
        </ContractSection>

        <ContractSection number="9" title="SIGNATURE BLOCK">
          <ContractSignatureBlock model={model} />
        </ContractSection>

        <View style={contractStyles.contractSpacer} />
        <ContractFooter model={model} />
      </Page>
    </Document>
  );
}

const GENERAL_ORTHO_CONTRACT_CLAUSES = [
  "The patient agrees to follow all orthodontic instructions given by the attending dentist and clinic team.",
  "The patient understands that good oral hygiene is required throughout treatment and that poor hygiene may delay or compromise treatment.",
  "The patient agrees to attend scheduled appointments on time and to notify the clinic in advance when rescheduling is necessary.",
  "The patient understands that missed appointments, repeated delays, appliance breakage, or non-compliance may extend the estimated treatment duration.",
  "The patient agrees to inform the clinic of pain, swelling, broken appliances, medical changes, or any concern that may affect treatment.",
  "The patient understands that final treatment results depend on biological response, cooperation, hygiene, and regular attendance.",
];

const TREATMENT_DURATION_CLAUSES = [
  "Estimated treatment duration is an approximation and may change depending on tooth movement, growth, compliance, hygiene, missed visits, or appliance breakage.",
  "Additional procedures, repairs, replacement appliances, retainers, or extensions may require separate charges if they are outside the agreed package terms.",
  "The clinic will explain material changes in treatment timing or scope when they become clinically apparent.",
];

const PAYMENT_RECEIPT_CLAUSES = [
  "The package price, down payment, remaining balance, installment count, and per-visit charge are based on the billing terms recorded for this orthodontic case.",
  "Official receipts are issued separately by the clinic according to its receipt process and only for confirmed payments actually received.",
  "Missed appointments do not automatically waive scheduled payment obligations unless the clinic confirms a billing adjustment in writing.",
];

const contractStyles = StyleSheet.create({
  page: {
    fontFamily: "Inter",
    fontSize: 7.6,
    color: "#111111",
    paddingTop: 28,
    paddingHorizontal: 34,
    paddingBottom: 24,
    flexDirection: "column",
  },
  header: { flexDirection: "row", justifyContent: "space-between", alignItems: "flex-start" },
  headerLeft: { width: 145 },
  contractLogo: {
    width: 58,
    height: 30,
    borderWidth: 1,
    borderColor: "#111111",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 3,
  },
  contractLogoImage: { width: 58, height: 30, objectFit: "contain", marginBottom: 3 },
  contractLogoText: { fontSize: 7, fontFamily: "Inter", fontWeight: TOK.weight.medium, letterSpacing: 0.8 },
  headerClinicName: { fontSize: 8.5, fontFamily: "Inter", fontWeight: TOK.weight.medium },
  contractTitle: { fontSize: 13, fontFamily: "Inter", fontWeight: TOK.weight.medium, letterSpacing: 1.1, textAlign: "right", marginTop: 4 },
  doubleRuleWrap: { marginTop: 7, marginBottom: 6 },
  ruleDark: { height: 1.2, backgroundColor: "#111111", marginBottom: 2 },
  ruleLight: { height: 0.7, backgroundColor: "#777777" },
  section: { marginTop: 4.5 },
  sectionTitle: {
    fontSize: 7.6,
    fontFamily: "Inter",
    fontWeight: TOK.weight.medium,
    letterSpacing: 0.45,
    marginBottom: 3,
  },
  fieldTable: { borderWidth: 0.7, borderColor: "#555555" },
  fieldRow: { flexDirection: "row", borderTopWidth: 0.7, borderTopColor: "#555555" },
  fieldRowFirst: { flexDirection: "row" },
  fieldBlock: { flex: 1, minHeight: 26, paddingHorizontal: 5, paddingVertical: 4, borderLeftWidth: 0.7, borderLeftColor: "#555555" },
  fieldBlockFirst: { flex: 1, minHeight: 26, paddingHorizontal: 5, paddingVertical: 4 },
  fieldLabel: { fontSize: 5.8, color: "#555555", textTransform: "uppercase", marginBottom: 3, letterSpacing: 0.35 },
  fieldValue: { fontSize: 8.2, fontFamily: "Inter", fontWeight: TOK.weight.medium, color: "#111111" },
  billingTable: { borderWidth: 0.7, borderColor: "#555555" },
  billingRow: { flexDirection: "row", borderTopWidth: 0.7, borderTopColor: "#555555" },
  billingRowFirst: { flexDirection: "row" },
  billingLabel: { width: 190, paddingVertical: 3.7, paddingHorizontal: 6, fontFamily: "Inter", fontWeight: TOK.weight.medium, borderRightWidth: 0.7, borderRightColor: "#555555" },
  billingValue: { flex: 1, paddingVertical: 3.7, paddingHorizontal: 6, textAlign: "right" },
  billingInverted: { backgroundColor: "#222222" },
  billingInvertedText: { color: "#ffffff", fontFamily: "Inter", fontWeight: TOK.weight.medium },
  clauseRow: { flexDirection: "row", marginBottom: 2.4 },
  clauseNumber: { width: 14, fontSize: 7.1 },
  clauseText: { flex: 1, fontSize: 7.1, lineHeight: 1.25 },
  grayBox: { backgroundColor: "#eeeeee", borderWidth: 0.7, borderColor: "#c8c8c8", padding: 6, fontSize: 7.2, lineHeight: 1.25 },
  signatureGrid: { flexDirection: "row", gap: 36, marginTop: 8 },
  signatureCol: { flex: 1 },
  signatureLineContract: { height: 1, backgroundColor: "#111111", marginTop: 10, marginBottom: 4 },
  signatureRoleContract: { fontSize: 7, fontFamily: "Inter", fontWeight: TOK.weight.medium, textAlign: "center" },
  printedName: { fontSize: 7, textAlign: "center", marginTop: 7 },
  dateLine: { borderTopWidth: 0.8, borderTopColor: "#111111", marginTop: 14, paddingTop: 3, fontSize: 7, textAlign: "center" },
  conforme: { marginTop: 9, fontFamily: "Times-Italic", fontSize: 8, textAlign: "center" },
  contractSpacer: { flexGrow: 1 },
  footer: { marginTop: 7 },
  footerRuleOne: { height: 1, backgroundColor: "#111111", marginBottom: 2 },
  footerRuleTwo: { height: 0.7, backgroundColor: "#777777", marginBottom: 5 },
  footerInner: { flexDirection: "row", justifyContent: "space-between" },
  footerText: { fontSize: 6.5, color: "#333333" },
});

function ContractHeader({ model }) {
  return (
    <View wrap={false}>
      <View style={contractStyles.header}>
        <View style={contractStyles.headerLeft}>
          {model.header.logoDataUrl ? (
            <Image src={model.header.logoDataUrl} style={contractStyles.contractLogoImage} />
          ) : (
            <View style={contractStyles.contractLogo}>
              <Text style={contractStyles.contractLogoText}>DENTAL OS</Text>
            </View>
          )}
          <Text style={contractStyles.headerClinicName}>{model.header.clinicName}</Text>
        </View>
        <Text style={contractStyles.contractTitle}>ORTHODONTIC TREATMENT CONTRACT</Text>
      </View>
      <View style={contractStyles.doubleRuleWrap}>
        <View style={contractStyles.ruleDark} />
        <View style={contractStyles.ruleLight} />
      </View>
    </View>
  );
}

function ContractSection({ number, title, children }) {
  return (
    <View style={contractStyles.section} wrap={false}>
      <Text style={contractStyles.sectionTitle}>{number}.  {title}</Text>
      {children}
    </View>
  );
}

function ContractField({ field }) {
  return (
    <View style={contractStyles.fieldBlock}>
      <Text style={contractStyles.fieldLabel}>{field.label}</Text>
      <Text style={contractStyles.fieldValue}>{field.value}</Text>
    </View>
  );
}

function ContractBillingRow({ row, first }) {
  const rowStyle = [
    first ? contractStyles.billingRowFirst : contractStyles.billingRow,
    row.inverted ? contractStyles.billingInverted : null,
  ];
  const textStyle = row.inverted ? contractStyles.billingInvertedText : null;
  return (
    <View style={rowStyle}>
      <Text style={[contractStyles.billingLabel, textStyle]}>{row.label}</Text>
      <Text style={[contractStyles.billingValue, textStyle]}>{row.value}</Text>
    </View>
  );
}

function NumberedClauses({ clauses }) {
  return (
    <View>
      {clauses.map((clause, index) => (
        <View key={`${index}-${clause.slice(0, 12)}`} style={contractStyles.clauseRow}>
          <Text style={contractStyles.clauseNumber}>{index + 1}.</Text>
          <Text style={contractStyles.clauseText}>{clause}</Text>
        </View>
      ))}
    </View>
  );
}

function ContractSignatureBlock({ model }) {
  return (
    <View>
      <View style={contractStyles.signatureGrid}>
        <View style={contractStyles.signatureCol}>
          <View style={contractStyles.signatureLineContract} />
          <Text style={contractStyles.signatureRoleContract}>{model.signatures.patientRole}</Text>
          <Text style={contractStyles.printedName}>Printed Name: {model.signatures.patientName}</Text>
          <Text style={contractStyles.dateLine}>Date</Text>
        </View>
        <View style={contractStyles.signatureCol}>
          <View style={contractStyles.signatureLineContract} />
          <Text style={contractStyles.signatureRoleContract}>Attending Dentist</Text>
          <Text style={contractStyles.printedName}>Printed Name: {model.signatures.doctorName}</Text>
          <Text style={contractStyles.dateLine}>Date</Text>
        </View>
      </View>
      <Text style={contractStyles.conforme}>Conforme: I have read, understood, and agree to the terms stated above.</Text>
    </View>
  );
}

function ContractFooter({ model }) {
  return (
    <View style={contractStyles.footer} fixed>
      <View style={contractStyles.footerRuleOne} />
      <View style={contractStyles.footerRuleTwo} />
      <View style={contractStyles.footerInner}>
        <Text style={contractStyles.footerText}>{model.footer.contractRef}</Text>
        <Text style={contractStyles.footerText} render={({ pageNumber }) => `Page ${pageNumber}`} />
      </View>
    </View>
  );
}

function labelize(value) {
  return String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function SectionLabel({ children }) {
  return (
    <View style={styles.sectionLabelRow}>
      <Text style={styles.sectionLabelText}>{String(children).toUpperCase()}</Text>
      <View style={styles.sectionRule} />
    </View>
  );
}

function PaymentOptionsSection({ enabledPlans, netTotal, phaseCount, flexMinDownPct }) {
  const enabledSet = new Set(enabledPlans?.length ? enabledPlans : []);
  const plans = PLAN_ORDER.filter((p) => enabledSet.has(p));
  if (plans.length === 0) return null;
  // 2-column bordered table. No filled header per the wireframe — the
  // labels carry the meaning. Each row is atomic via wrap={false}.
  return (
    <View style={styles.section}>
      <View wrap={false}>
        <SectionLabel>Payment options</SectionLabel>
        <View style={styles.table}>
          <PaymentRow
            plan={plans[0]}
            isFirst={true}
            netTotal={netTotal}
            phaseCount={phaseCount}
            flexMinDownPct={flexMinDownPct}
          />
        </View>
      </View>
      {plans.length > 1 ? (
        <View style={[styles.table, { borderTopWidth: 0 }]}>
          {plans.slice(1).map((p) => (
            <PaymentRow
              key={p}
              plan={p}
              isFirst={false}
              netTotal={netTotal}
              phaseCount={phaseCount}
              flexMinDownPct={flexMinDownPct}
            />
          ))}
        </View>
      ) : null}
    </View>
  );
}

function PaymentRow({ plan, isFirst, netTotal, phaseCount, flexMinDownPct }) {
  return (
    <View
      style={isFirst ? styles.tableBodyRowFirst : styles.tableBodyRow}
      wrap={false}
    >
      <Text
        style={[
          styles.tableBodyCell,
          {
            width: 180,
            borderRightWidth: 1,
            borderRightColor: TOK.color.hairline,
            fontFamily: "Inter",
            fontWeight: TOK.weight.medium,
          },
        ]}
      >
        {PLAN_TITLE[plan] || plan}
      </Text>
      <Text style={[styles.tableBodyCell, { flex: 1 }]}>
        {planBodyLine(plan, { netTotal, phaseCount, flexMinDownPct })}
      </Text>
    </View>
  );
}

// Column flex weights for the procedure table (TOOTH / PROCEDURE /
// CODE / UNITS / PRICE / TOTAL). Per the spec wireframe and
// proportions.
const SCOPE_COLS = [10, 38, 14, 8, 14, 16];

function ScopeOfTreatmentSection({ items }) {
  const rows = sortRowsByTooth(buildSinglePlanRows(items));
  const total = rows.reduce((s, r) => s + rowNetCentavos(r), 0);

  return (
    <View style={styles.section}>
      <View wrap={false}>
        <SectionLabel>Scope of treatment</SectionLabel>
        <View style={styles.table}>
          {/* Filled accent header — atomic with first row via the
              wrap={false} on this enclosing keep-with-next View. */}
          <View style={styles.tableHeadFilled}>
            {["TOOTH #", "PROCEDURE", "CODE", "UNITS", "PRICE", "TOTAL"].map((h, i) => (
              <Text
                key={h}
                style={[
                  styles.tableHeadFilledCell,
                  { flex: SCOPE_COLS[i], textAlign: i >= 3 ? "right" : "left" },
                ]}
              >
                {h}
              </Text>
            ))}
          </View>
          {rows.length === 0 ? (
            <View style={styles.tableBodyRow}>
              <Text
                style={[
                  styles.tableBodyCell,
                  { flex: 1, color: TOK.color.secondary, fontStyle: "italic" },
                ]}
              >
                No items in this plan.
              </Text>
            </View>
          ) : (
            <ScopeRow row={rows[0]} isFirst={true} />
          )}
        </View>
      </View>
      {rows.length > 1 ? (
        <View style={[styles.table, { borderTopWidth: 0 }]}>
          {rows.slice(1).map((r, idx) => (
            <ScopeRow key={idx} row={r} isFirst={false} />
          ))}
        </View>
      ) : null}
      {rows.length > 0 ? (
        // GRAND TOTAL row — kept with the row immediately preceding it
        // so the total never orphans on a continuation page.
        <View
          style={[styles.table, { borderTopWidth: 0 }]}
          wrap={false}
        >
          <View style={styles.grandTotalRow}>
            <Text
              style={[
                styles.grandTotalLabelCell,
                {
                  // Span the first 5 columns so "GRAND TOTAL" sits in
                  // the PRICE column visually, leaving the value in
                  // the TOTAL column.
                  flex: SCOPE_COLS.slice(0, 5).reduce((s, n) => s + n, 0),
                },
              ]}
            >
              GRAND TOTAL
            </Text>
            <Text
              style={[
                styles.grandTotalValueCell,
                { flex: SCOPE_COLS[5] },
              ]}
            >
              {formatPHP(total)}
            </Text>
          </View>
        </View>
      ) : null}
    </View>
  );
}

function ScopeRow({ row, isFirst }) {
  return (
    <View
      style={isFirst ? styles.tableBodyRowFirst : styles.tableBodyRow}
      wrap={false}
    >
      <Text style={[styles.tableBodyCell, { flex: SCOPE_COLS[0] }]}>{row.tooth}</Text>
      <Text style={[styles.tableBodyCell, { flex: SCOPE_COLS[1] }]}>{row.procedure}</Text>
      <Text style={[styles.tableBodyCell, { flex: SCOPE_COLS[2], fontSize: 9 }]}>{row.code}</Text>
      <Text style={[styles.tableBodyCell, { flex: SCOPE_COLS[3], textAlign: "right" }]}>{String(row.units)}</Text>
      <Text style={[styles.tableBodyCell, { flex: SCOPE_COLS[4], textAlign: "right" }]}>{formatPHP(row.unitFee)}</Text>
      <Text style={[styles.tableBodyCell, { flex: SCOPE_COLS[5], textAlign: "right" }]}>{formatPHP(rowNetCentavos(row))}</Text>
    </View>
  );
}

// Visit schedule table column proportions: VISIT (narrow centered) |
// TIME (narrow centered) | ACTIVITIES (flex).
const VISIT_COLS = [12, 14, 74];

function VisitScheduleSection({ sections }) {
  if (sections.length === 0) return null;
  return (
    <View style={styles.section}>
      <View wrap={false}>
        <SectionLabel>Visit schedule</SectionLabel>
        <View style={styles.table}>
          <View style={styles.tableHeadFilled}>
            {["VISIT", "TIME", "ACTIVITIES"].map((h, i) => (
              <Text
                key={h}
                style={[
                  styles.tableHeadFilledCell,
                  { flex: VISIT_COLS[i], textAlign: i < 2 ? "center" : "left" },
                ]}
              >
                {h}
              </Text>
            ))}
          </View>
          {sections[0] ? <VisitTableRow sec={sections[0]} index={0} isFirst={true} /> : null}
        </View>
      </View>
      {sections.length > 1 ? (
        <View style={[styles.table, { borderTopWidth: 0 }]}>
          {sections.slice(1).map((sec, i) => (
            <VisitTableRow key={i + 1} sec={sec} index={i + 1} isFirst={false} />
          ))}
        </View>
      ) : null}
    </View>
  );
}

function VisitTableRow({ sec, index, isFirst }) {
  const activities = sec.visits.map((v) => {
    const it = v.item || {};
    const procLabel = it.display_label || it.procedure_name || "Procedure";
    const teethArr  = Array.isArray(it.teeth) ? it.teeth : [];
    const tooth     = teethArr.length >= 2
      ? teethListString(it)
      : (it.tooth_number ? String(it.tooth_number) : "");
    const stage = !v.is_synthetic && v.label && v.label !== procLabel ? v.label : null;
    if (stage) return tooth ? `${stage} — ${procLabel} (${tooth})` : `${stage} — ${procLabel}`;
    return tooth ? `${procLabel} on tooth #${tooth}` : procLabel;
  });

  return (
    <View
      style={isFirst ? styles.tableBodyRowFirst : styles.tableBodyRow}
      wrap={false}
    >
      <Text
        style={[
          styles.tableBodyCell,
          {
            flex: VISIT_COLS[0],
            textAlign: "center",
            fontFamily: "Inter",
            fontWeight: TOK.weight.medium,
            fontSize: TOK.font.visitNum,
          },
        ]}
      >
        {index + 1}
      </Text>
      <Text
        style={[
          styles.tableBodyCell,
          { flex: VISIT_COLS[1], textAlign: "center" },
        ]}
      >
        {sec.durationMin > 0 ? `${sec.durationMin} min` : "—"}
      </Text>
      <View style={[{ flex: VISIT_COLS[2], paddingVertical: TOK.s[6], paddingHorizontal: TOK.s[10] }]}>
        {sec.timing ? (
          <Text style={{ fontSize: TOK.font.tableBody, color: TOK.color.primary, marginBottom: 3 }}>
            {sec.timing.charAt(0).toUpperCase() + sec.timing.slice(1)}
          </Text>
        ) : null}
        {activities.length === 0 ? (
          <Text style={{ fontSize: TOK.font.tableBody, color: TOK.color.secondary, fontStyle: "italic" }}>
            (no activities scheduled)
          </Text>
        ) : (
          activities.map((a, j) => (
            <Text
              key={j}
              style={{
                fontSize: TOK.font.tableBody,
                color: TOK.color.primary,
                lineHeight: 1.4,
                marginBottom: 1,
              }}
            >
              · {a}
            </Text>
          ))
        )}
      </View>
    </View>
  );
}

function PageBottomUnit({ patient, doctorName, doctorPrcLicense, clinic, validityStr, signature }) {
  // The Acknowledgment paragraph + asterisk footnotes + Conforme block
  // + footer form a single page-bottom unit. Atomic via wrap={false}.
  // Pinned to the bottom of the page via the flex-grow spacer that
  // sits immediately above this in <TxPlanDocument>.
  const phone = clinic?.phone;
  const clinicName = clinic?.clinic_name || clinic?.name;
  const contact = phone && clinicName
    ? `For questions: ${clinicName} · ${phone}`
    : phone ? `For questions: ${phone}`
    : clinicName ? `For questions: ${clinicName}`
    : null;
  const doctorPrc = normalizePrc(doctorPrcLicense);

  return (
    <View wrap={false}>
      <SectionLabel>Acknowledgment</SectionLabel>
      <Text style={styles.ackText}>
        The patient acknowledges the treatment plan and pricing above. Final payment terms are agreed at the first visit.
      </Text>
      <View style={styles.footnotesWrap}>
        {validityStr ? (
          <Text style={styles.footnoteLine}>*Quote valid through {validityStr}.</Text>
        ) : null}
        <Text style={styles.footnoteLine}>
          *Estimate may vary based on clinical findings during treatment.
        </Text>
        <Text style={styles.footnoteLine}>
          *Payment plans subject to clinic approval at checkout.
        </Text>
      </View>

      {/* Conforme block — formal signature convention common in PH
          dental clinics. Patient name + doctor name printed below
          the signature line; role label below in uppercase. */}
      <View style={{ marginTop: TOK.s[20] }}>
        <Text style={styles.confLabel}>Conforme:</Text>
        <View style={styles.signatureRow}>
          <View style={styles.signatureCol}>
            <View style={styles.signatureSpacer}>
              {signature?.imageDataUrl ? (
                <Image src={signature.imageDataUrl} style={styles.signatureImage} />
              ) : null}
            </View>
            <View style={styles.signatureLine} />
            <Text style={styles.signatureName}>{signature?.signerName || fullName(patient)}</Text>
            <Text style={styles.signatureRole}>
              {signature?.signerRole && signature.signerRole !== "patient"
                ? signature.signerRole.toUpperCase()
                : "PATIENT"}
            </Text>
            {signature?.staffWitnessName ? (
              <Text style={styles.signedMeta}>Witnessed by {signature.staffWitnessName}</Text>
            ) : null}
          </View>
          <View style={styles.signatureCol}>
            <View style={styles.signatureSpacer} />
            <View style={styles.signatureLine} />
            <Text style={styles.signatureName}>{doctorName}</Text>
            {doctorPrc ? <Text style={styles.signaturePrc}>{`PRC ${doctorPrc}`}</Text> : null}
            <Text style={styles.signatureRole}>ATTENDING DENTIST</Text>
          </View>
        </View>
      </View>

      <View style={styles.footerRule} />
      <View style={styles.footerInner}>
        {contact ? <Text style={styles.footerLine}>{contact}</Text> : null}
      </View>
    </View>
  );
}

// ─── Document ────────────────────────────────────────────────────────
// Compute the per-plan summary numbers used in single + compare layouts.
function planSummary(plan) {
  const items = openItems(plan);
  const rows  = buildSinglePlanRows(items);
  const netTotal = rows.reduce((s, r) => s + rowNetCentavos(r), 0);

  const visits = expandPlanToVisits({ items });
  const phaseSet = new Set();
  for (const v of visits) {
    if (v.phase_number != null && Number(v.phase_number) > 0) phaseSet.add(Number(v.phase_number));
  }
  for (const it of items) {
    if (it.phase_number != null && Number(it.phase_number) > 0) phaseSet.add(Number(it.phase_number));
  }
  for (const k of Object.keys(plan?.phase_names || {})) {
    const n = Number(k);
    if (Number.isFinite(n) && n > 0) phaseSet.add(n);
  }
  const phaseCount = phaseSet.size || 1;
  const chairMin   = visits.reduce((s, v) => s + (Number(v.chair_min) || 0), 0);
  const sections   = visitSections(plan, items);
  return { items, netTotal, phaseCount, chairMin, sections };
}

// Side-by-side summary band for compare mode. Mirrors the
// institutional table treatment used elsewhere — two-cell row with
// filled accent header (Option A | Option B), tinted-row body holding
// each option's total and supporting line.
function CompareSummaryBand({ planA, planB }) {
  return (
    <View style={[styles.section, styles.table]} wrap={false}>
      <View style={styles.tableHeadFilled}>
        <Text style={[styles.tableHeadFilledCell, { flex: 1, textAlign: "center" }]}>OPTION A</Text>
        <Text
          style={[
            styles.tableHeadFilledCell,
            { flex: 1, textAlign: "center", borderLeftWidth: 1, borderLeftColor: TOK.color.hairline },
          ]}
        >
          OPTION B
        </Text>
      </View>
      <View style={[styles.tableBodyRowFirst, { backgroundColor: TOK.color.tintedRow }]}>
        <CompareSummaryCell plan={planA} hasLeftBorder={false} />
        <CompareSummaryCell plan={planB} hasLeftBorder={true} />
      </View>
    </View>
  );
}

function CompareSummaryCell({ plan, hasLeftBorder }) {
  const { netTotal, phaseCount, chairMin } = planSummary(plan);
  const visitsLabel = `${phaseCount} ${phaseCount === 1 ? "visit" : "visits"}`;
  const chairLabel  = chairMin > 0 ? `~${chairMin} min` : null;
  const subline = [visitsLabel, chairLabel].filter(Boolean).join(" · ");
  return (
    <View
      style={{
        flex: 1,
        paddingVertical: TOK.s[12],
        paddingHorizontal: TOK.s[16],
        borderLeftWidth: hasLeftBorder ? 1 : 0,
        borderLeftColor: TOK.color.hairline,
        alignItems: "center",
      }}
    >
      <Text
        style={{
          fontFamily: "Inter",
          fontWeight: TOK.weight.medium,
          fontSize: TOK.font.grandTotal,
          color: TOK.color.primary,
        }}
      >
        {formatPHP(netTotal)}
      </Text>
      {subline ? (
        <Text
          style={{
            fontSize: TOK.font.body,
            color: TOK.color.secondary,
            marginTop: 2,
          }}
        >
          {subline}
        </Text>
      ) : null}
    </View>
  );
}

// "OPTION A" / "OPTION B" divider — same chrome as SectionLabel but
// slightly more prominent (larger letter spacing, thicker rule). Used
// to mark the start of each option's full body in compare mode.
function OptionDivider({ children }) {
  return (
    <View style={[styles.sectionLabelRow, { marginTop: TOK.s[28] }]}>
      <Text style={[styles.sectionLabelText, { letterSpacing: 1.6 }]}>
        {String(children).toUpperCase()}
      </Text>
      <View style={styles.sectionRule} />
    </View>
  );
}

function TxPlanCompareDocument({ plans, patient, clinic, doctorName, doctorPrcLicense }) {
  const [planA, planB] = plans;
  const aSummary = planSummary(planA);
  const bSummary = planSummary(planB);

  // Plan creation date — use the more recent of the two (or whichever
  // has a created_at); the date on the PDF reflects when this comparison
  // is being presented to the patient.
  const aDate = planA?.created_at ? new Date(planA.created_at) : null;
  const bDate = planB?.created_at ? new Date(planB.created_at) : null;
  const newest = [aDate, bDate].filter(Boolean).sort((x, y) => y - x)[0] || new Date();
  const dateStr = formatLongDate(newest);

  // Validity stamp uses the same clinic-driven `quote_validity_days`
  // as single-plan mode, anchored to the newer plan's created_at.
  const validityDays = Number(clinic?.quote_validity_days) || DEFAULT_QUOTE_VALIDITY_DAYS;
  const validityDate = new Date(newest);
  validityDate.setDate(validityDate.getDate() + Math.max(1, validityDays));
  const validityStr = formatLongDate(validityDate);

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <Letterhead clinic={clinic} />
        <TitleBlock
          patient={patient}
          doctorName={doctorName}
          doctorPrcLicense={doctorPrcLicense}
          dateStr={dateStr}
          mode="compare"
        />
        <CompareSummaryBand planA={planA} planB={planB} />

        <OptionDivider>Option A</OptionDivider>
        <ScopeOfTreatmentSection items={aSummary.items} />
        <VisitScheduleSection sections={aSummary.sections} />

        <OptionDivider>Option B</OptionDivider>
        <ScopeOfTreatmentSection items={bSummary.items} />
        <VisitScheduleSection sections={bSummary.sections} />

        {/* Compare mode omits the per-clinic payment options block —
            payment terms only matter once the patient picks an option,
            and at that point the single-plan PDF (or checkout) carries
            the schedule. */}

        <View style={styles.spacer} />
        <PageBottomUnit
          patient={patient}
          doctorName={doctorName}
          doctorPrcLicense={doctorPrcLicense}
          clinic={clinic}
          validityStr={validityStr}
        />
      </Page>
    </Document>
  );
}

function TxPlanDocument({ plan, patient, clinic, doctorName, doctorPrcLicense, signature }) {
  const items = openItems(plan);
  const rows  = buildSinglePlanRows(items);
  const netTotal = rows.reduce((s, r) => s + rowNetCentavos(r), 0);

  const visits = expandPlanToVisits({ items });
  const phaseSet = new Set();
  for (const v of visits) {
    if (v.phase_number != null && Number(v.phase_number) > 0) phaseSet.add(Number(v.phase_number));
  }
  for (const it of items) {
    if (it.phase_number != null && Number(it.phase_number) > 0) phaseSet.add(Number(it.phase_number));
  }
  for (const k of Object.keys(plan?.phase_names || {})) {
    const n = Number(k);
    if (Number.isFinite(n) && n > 0) phaseSet.add(n);
  }
  const phaseCount = phaseSet.size || 1;
  const chairMin   = visits.reduce((s, v) => s + (Number(v.chair_min) || 0), 0);
  const sections   = visitSections(plan, items);

  const enabledPlans =
    Array.isArray(clinic?.payment_schedules_enabled) && clinic.payment_schedules_enabled.length > 0
      ? clinic.payment_schedules_enabled
      : ["per_phase"];
  const flexMinDownPct = Number(clinic?.flexible_min_down_pct) || 0;

  // Plan creation date drives both the header date and the validity
  // calculation. Falls back to today if missing (rare — every saved
  // plan has a created_at).
  const planCreatedAt = plan?.created_at ? new Date(plan.created_at) : new Date();
  const dateStr = formatLongDate(planCreatedAt);
  const accepted = ["Accepted", "Needs Booking", "Scheduled", "In Progress", "AR", "Completed"]
    .includes(plan?.status);
  const validityDays = Number(clinic?.quote_validity_days) || DEFAULT_QUOTE_VALIDITY_DAYS;
  const validityDate = new Date(planCreatedAt);
  validityDate.setDate(validityDate.getDate() + Math.max(1, validityDays));
  const validityStr = formatLongDate(validityDate);

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <Letterhead clinic={clinic} />
        <TitleBlock
          patient={patient}
          doctorName={doctorName}
          doctorPrcLicense={doctorPrcLicense}
          dateStr={dateStr}
          accepted={accepted}
        />
        <ScopeOfTreatmentSection items={items} />
        <PaymentOptionsSection
          enabledPlans={enabledPlans}
          netTotal={netTotal}
          phaseCount={phaseCount}
          flexMinDownPct={flexMinDownPct}
        />
        <VisitScheduleSection sections={sections} />
        {/* Flex-grow spacer pins the page-bottom unit to the bottom of
            the page it lands on. The unit (acknowledgment + footnotes +
            Conforme + signatures + footer) is atomic via wrap={false}. */}
        <View style={styles.spacer} />
        <PageBottomUnit
          patient={patient}
          doctorName={doctorName}
          doctorPrcLicense={doctorPrcLicense}
          clinic={clinic}
          validityStr={validityStr}
          signature={signature}
        />
      </Page>
    </Document>
  );
}

// ─── Public API ──────────────────────────────────────────────────────
function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

async function blobToBase64(blob) {
  const buf = await blob.arrayBuffer();
  const bytes = new Uint8Array(buf);
  let binary = "";
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunkSize));
  }
  return btoa(binary);
}

export async function buildTreatmentPlanPdf({
  plan, plans, patient, clinic, doctorName, doctorPrcLicense, signature,
}) {
  if (!String(doctorName || "").trim()) {
    throw new Error(
      "Doctor name is required before generating a treatment plan. Set it in Settings → Practitioners."
    );
  }
  if (!String(doctorPrcLicense || "").trim()) {
    throw new Error(
      "Please complete the attending doctor's PRC number before generating a treatment plan."
    );
  }

  // Compare-mode dispatch: when the caller passes two plans, render the
  // side-by-side comparison document. Otherwise render the single-plan
  // document. Both share the letterhead, title block, acknowledgment +
  // signatures, and footer.
  const isCompare = Array.isArray(plans) && plans.length >= 2 && !!plans[0] && !!plans[1];
  if (!isCompare && !plan && !(plans?.length)) {
    throw new Error("Treatment plan is required to generate a PDF.");
  }

  const element = isCompare ? (
    <TxPlanCompareDocument
      plans={plans.slice(0, 2)}
      patient={patient}
      clinic={clinic}
      doctorName={doctorName}
      doctorPrcLicense={doctorPrcLicense}
    />
  ) : (
    <TxPlanDocument
      plan={plan || plans[0]}
      patient={patient}
      clinic={clinic}
      doctorName={doctorName}
      doctorPrcLicense={doctorPrcLicense}
      signature={signature}
    />
  );

  const blob = await pdf(element).toBlob();
  let cachedBase64 = null;

  return {
    blob,
    save: (filename) => downloadBlob(blob, filename),
    async _toBase64() {
      if (cachedBase64) return cachedBase64;
      cachedBase64 = await blobToBase64(blob);
      return cachedBase64;
    },
  };
}

export async function buildOrthoContractPdf({
  contract, patient, clinic, doctorName, doctorPrcLicense,
}) {
  if (!contract) {
    throw new Error("Ortho contract data is required to generate a PDF.");
  }

  const element = (
    <OrthoContractDocument
      contract={contract}
      patient={patient}
      clinic={clinic}
      doctorName={doctorName}
      doctorPrcLicense={doctorPrcLicense}
    />
  );

  const blob = await pdf(element).toBlob();
  let cachedBase64 = null;

  return {
    blob,
    save: (filename) => downloadBlob(blob, filename),
    async _toBase64() {
      if (cachedBase64) return cachedBase64;
      cachedBase64 = await blobToBase64(blob);
      return cachedBase64;
    },
  };
}

export async function pdfToBase64(doc) {
  return await doc._toBase64();
}

export function pdfFilename(patient, kind = "plan") {
  const safe = (patient?.patient_number || "plan").replace(/[^A-Za-z0-9_-]/g, "");
  if (kind === "ortho_contract") return `ortho_contract_${safe}.pdf`;
  return kind === "comparison"
    ? `treatment_comparison_${safe}.pdf`
    : `treatment_plan_${safe}.pdf`;
}
